import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  hasStoredLoyaltyAuthHints,
  shouldUseAggressiveRestore,
} from '../utils/loyaltySessionRestore';
import {
  isLikelyQrOrExternalOpen,
  probeLoyaltySessionAndRedirect,
} from '../utils/loyaltyEarlyAuth';

const PAGE_SHOW_RETRY_MS = 500;

/**
 * Secondary check from React after the login form mounts.
 * Primary QR / bfcache handling lives in installLoyaltyEarlyAuth() (index.js).
 */
export default function useLoyaltyAutoLogin() {
  const location = useLocation();
  const [checking, setChecking] = useState(
    () => hasStoredLoyaltyAuthHints() || shouldUseAggressiveRestore(),
  );
  const runIdRef = useRef(0);

  useEffect(() => {
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    let cancelled = false;

    const runCheck = async () => {
      const shouldProbe =
        hasStoredLoyaltyAuthHints() || shouldUseAggressiveRestore();

      if (!shouldProbe) {
        if (!cancelled && runIdRef.current === runId) {
          setChecking(false);
        }
        return;
      }

      if (!cancelled && runIdRef.current === runId) {
        setChecking(true);
      }

      try {
        await probeLoyaltySessionAndRedirect(location.search);
      } catch {
        // Network blip — user can still log in manually.
      }

      if (!cancelled && runIdRef.current === runId) {
        setChecking(false);
      }
    };

    const delay = isLikelyQrOrExternalOpen() ? PAGE_SHOW_RETRY_MS : 0;
    const startId = window.setTimeout(runCheck, delay);

    const onPageShow = () => {
      window.setTimeout(runCheck, PAGE_SHOW_RETRY_MS);
    };

    window.addEventListener('pageshow', onPageShow);

    return () => {
      cancelled = true;
      window.clearTimeout(startId);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [location.search]);

  return { checking };
}
